import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Calculator,
  TrendingUp,
  MessageSquare,
  BarChart2,
  DollarSign,
  Sparkles,
  PenTool,
  Clock,
  FileQuestion,
  Files
} from 'lucide-react';

import { accountingCurriculum } from '@/data/accountingCurriculum';
import { accountingWeeklyLessons } from '@/data/accountingWeeklyLessons';
import { 
  year11AccountingUnitTests,
  year12AccountingUnitTests,
  year13AccountingUnitTests
} from '@/data/accountingUnitTests';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { getCurrentUser } from '@/data/users';

export default function AccountingCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [teacherYears, setTeacherYears] = useState(['11', '12', '13']);
  
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser?.classes) {
      const years = currentUser.classes
        .filter(c => c.toLowerCase().includes('accounting'))
        .map(c => {
          const match = c.match(/year\s*(\d+)/i);
          return match ? match[1] : null;
        })
        .filter(Boolean);
      if (years.length > 0) {
        setTeacherYears([...new Set(years)]);
      }
    }
  }, []);

  const yearKey = selectedYear.toLowerCase().replace(' ', '');
  const curriculum = accountingCurriculum[yearKey] || accountingCurriculum.year11;

  // Get unit tests for selected year
  const getUnitTestsForYear = () => {
    const yearNum = parseInt(selectedYear.replace('Year ', ''));
    switch(yearNum) {
      case 11: return year11AccountingUnitTests;
      case 12: return year12AccountingUnitTests;
      case 13: return year13AccountingUnitTests;
      default: return year11AccountingUnitTests;
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      MessageSquare: MessageSquare,
      FileText: FileText,
      BarChart2: BarChart2,
      TrendingUp: TrendingUp,
      Calculator: Calculator,
      DollarSign: DollarSign
    };
    return icons[iconName] || BookOpen;
  };

  const getSkillLevelBadge = (level) => {
    const levels = {
      1: { label: 'Uni-Structural', color: 'bg-blue-100 text-blue-800' },
      2: { label: 'Multi-Structural', color: 'bg-green-100 text-green-800' },
      3: { label: 'Relational', color: 'bg-purple-100 text-purple-800' },
      4: { label: 'Extended Abstract', color: 'bg-orange-100 text-orange-800' }
    };
    const levelInfo = levels[level] || levels[1];
    return <Badge className={levelInfo.color}>{levelInfo.label}</Badge>;
  };

  const totalTopics = curriculum.strands.reduce((acc, strand) => {
    return acc + strand.subStrands.reduce((subAcc, subStrand) => {
      return subAcc + (subStrand.topics?.length || 0);
    }, 0);
  }, 0);

  return (
    <div className="p-6 space-y-6" data-testid="accounting-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-emerald-600" />
            Accounting Curriculum
          </h1>
          <p className="text-gray-600 mt-1">
            Senior Secondary (Years 11-13) - Vanuatu National Syllabus
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[140px]" data-testid="year-select">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Year 11">Year 11</SelectItem>
              <SelectItem value="Year 12">Year 12</SelectItem>
              <SelectItem value="Year 13">Year 13</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Printer className="h-4 w-4" />
            Print Syllabus
          </Button>
        </div>
      </div>

      {/* Year Overview Card */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-emerald-800">{curriculum.title}</h2>
              <p className="text-emerald-600 mt-1">{curriculum.description}</p>
            </div>
            <div className="flex gap-6 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-700">{curriculum.hours}</div>
                <div className="text-emerald-600">Hours</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-700">{curriculum.strands.length}</div>
                <div className="text-emerald-600">Strands</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-700">{totalTopics}</div>
                <div className="text-emerald-600">Topics</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 m-4">
            <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
              <BookOpen className="h-4 w-4" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
              <Clock className="h-4 w-4" />
              Scheme
            </TabsTrigger>
            <TabsTrigger value="strands" className="flex items-center gap-2" data-testid="strands-tab">
              <Layers className="h-4 w-4" />
              Strands
            </TabsTrigger>
            <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
              <FileQuestion className="h-4 w-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-emerald-100" data-testid="lesson-plans-tab">
              <PenTool className="h-4 w-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
              <Files className="h-4 w-4" />
              Quick Print
            </TabsTrigger>
            <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
              <Sparkles className="h-4 w-4" />
              AI Generator
            </TabsTrigger>
          </TabsList>

          {/* Schemes Tab - Weekly Scheme of Work */}
          <TabsContent value="schemes" className="p-4">
            <SubjectSchemeOfWork
              subject="Accounting"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={accountingWeeklyLessons}
              subjectColor="emerald"
            />
          </TabsContent>

          {/* Term Exam Tab */}
          <TabsContent value="term-exam" className="p-4">
            <TermExamGenerator
              subject="Accounting"
              year={parseInt(selectedYear.replace('Year ', ''))}
              unitTests={getUnitTestsForYear()}
              subjectColor="emerald"
            />
          </TabsContent>

          {/* Quick Print Tab */}
          <TabsContent value="quick-print" className="p-4">
            <QuickPrintAll
              subject="Accounting"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={accountingWeeklyLessons}
              subjectColor="emerald"
            />
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum" className="p-4">
            <div className="space-y-6">
              {curriculum.strands.map((strand) => {
                const StrandIcon = getIconComponent(strand.icon);
                return (
                  <Card key={strand.id} className="border-l-4" style={{ borderLeftColor: strand.color === 'blue' ? '#3b82f6' : strand.color === 'green' ? '#22c55e' : strand.color === 'purple' ? '#a855f7' : '#f97316' }}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg bg-${strand.color}-100`}>
                            <StrandIcon className={`h-5 w-5 text-${strand.color}-600`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{strand.name}</CardTitle>
                            <CardDescription>{strand.outcome}</CardDescription>
                          </div>
                        </div>
                        <div className="flex gap-4 text-sm">
                          <Badge variant="outline">{strand.hours} hours</Badge>
                          <Badge variant="secondary">{strand.weight}%</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Accordion type="multiple" className="space-y-2">
                        {strand.subStrands.map((subStrand) => (
                          <AccordionItem key={subStrand.id} value={subStrand.id} className="border rounded-lg">
                            <AccordionTrigger className="px-4 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-gray-500" />
                                <span className="font-medium">{subStrand.name}</span>
                                <Badge variant="outline" className="ml-2">
                                  {subStrand.topics?.length || 0} outcomes
                                </Badge>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                              <p className="text-sm text-gray-600 mb-4 italic">{subStrand.outcome}</p>
                              <div className="space-y-2">
                                {subStrand.topics?.map((topic, idx) => (
                                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <code className="text-xs bg-gray-200 px-1 rounded">{topic.code}</code>
                                        {getSkillLevelBadge(topic.level)}
                                      </div>
                                      <p className="text-sm text-gray-700 mt-1">{topic.topic}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Strands Overview Tab */}
          <TabsContent value="strands" className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {curriculum.strands.map((strand) => {
                const StrandIcon = getIconComponent(strand.icon);
                const totalOutcomes = strand.subStrands.reduce((acc, sub) => acc + (sub.topics?.length || 0), 0);
                
                return (
                  <Card key={strand.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl bg-${strand.color}-100`}>
                          <StrandIcon className={`h-6 w-6 text-${strand.color}-600`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{strand.name}</CardTitle>
                          <div className="flex gap-2 mt-1">
                            <Badge>{strand.hours} hours</Badge>
                            <Badge variant="outline">{strand.weight}% weighting</Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">{strand.outcome}</p>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Sub-strands</span>
                          <span className="font-medium">{strand.subStrands.length}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Learning Outcomes</span>
                          <span className="font-medium">{totalOutcomes}</span>
                        </div>
                        <Progress value={strand.weight} className="h-2 mt-2" />
                      </div>
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-gray-500 font-medium mb-2">Sub-strands:</p>
                        <div className="flex flex-wrap gap-1">
                          {strand.subStrands.map((sub) => (
                            <Badge key={sub.id} variant="secondary" className="text-xs">
                              {sub.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Lesson Plans Tab */}
          <TabsContent value="lesson-plans" className="p-4">
            <LessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              subject="Accounting"
              subjectColor="emerald"
              allowedYears={['11', '12', '13']}
              weeklyLessons={accountingWeeklyLessons}
            />
          </TabsContent>

          {/* AI Lesson Generator Tab */}
          <TabsContent value="ai-lesson" className="p-4">
            <AILessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              defaultSubject="accounting" 
              schoolType="secondary"
              allowedYears={teacherYears}
              allowedSubjects={['accounting']}
            />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Skill Levels Legend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            SOLO Taxonomy Skill Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Badge className="bg-blue-100 text-blue-800 mb-2">Level 1 - Uni-Structural</Badge>
              <p className="text-sm text-gray-600">Basic recall and recognition. Define, identify, list, name.</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Badge className="bg-green-100 text-green-800 mb-2">Level 2 - Multi-Structural</Badge>
              <p className="text-sm text-gray-600">Multiple aspects understood. Describe, complete, calculate.</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Badge className="bg-purple-100 text-purple-800 mb-2">Level 3 - Relational</Badge>
              <p className="text-sm text-gray-600">Connected understanding. Explain, compare, prepare, analyse.</p>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <Badge className="bg-orange-100 text-orange-800 mb-2">Level 4 - Extended Abstract</Badge>
              <p className="text-sm text-gray-600">Transfer and generalize. Discuss, evaluate, recommend, create.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
